import { CourseApi } from "@thepiquelab/web-api";
import { UploadFilePath, UploadResultDTO } from "@thepiquelab/web-types";
import { useRequest } from "ahooks";
import { RcFile } from "antd/es/upload";
import { useState } from "react";

export function uploadFileWithXHR({
  file,
  uploadUrl,
  onProgress,
  onSuccess,
  onError,
}: {
  file: File;
  uploadUrl: string;
  onProgress?: (percent: number) => void;
  onSuccess?: () => void;
  onError?: (err: any) => void;
}): void {
  const xhr = new XMLHttpRequest();

  xhr.upload.onprogress = (event) => {
    if (event.lengthComputable && onProgress) {
      const percent = Math.round((event.loaded / event.total) * 100);
      onProgress(percent);
    }
  };

  xhr.onload = () => {
    if (xhr.status >= 200 && xhr.status < 300) {
      onSuccess?.();
    } else {
      onError?.(new Error(`Upload failed: ${xhr.status}`));
    }
  };

  xhr.onerror = () => {
    onError?.(new Error("Network error during upload"));
  };

  xhr.open("PUT", uploadUrl);
  xhr.setRequestHeader("Content-Type", file.type);
  xhr.send(file);
}

export function uploadFileWithPromise({
  file,
  uploadUrl,
  onProgress,
}: {
  file: File;
  uploadUrl: string;
  onProgress?: (percent: number) => void;
}): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.statusText || xhr.status}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error("Network error during upload"));
    };

    xhr.onabort = () => {
      reject(new Error("Upload aborted"));
    };

    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.send(file);
  });
}

export const useUpload = (
  path: UploadFilePath,
): {
  upload: (file: RcFile) => Promise<UploadResultDTO>;
  loading: boolean;
  data: UploadResultDTO;
  progress: number;
} => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const {
    runAsync: uploadSign,
    data,
    mutate,
  } = useRequest(
    (path: UploadFilePath, fileName: string) =>
      CourseApi.uploadSign(path, fileName),
    {
      manual: true,
    },
  );

  const upload = async (file: RcFile): Promise<UploadResultDTO> => {
    try {
      setLoading(true);
      const result = await uploadSign(path, file.name);
      await uploadFileWithPromise({
        file,
        onProgress: (percent) => {
          setProgress(percent);
        },
        uploadUrl: result.signedUrl,
      });
      return result;
    } catch (error) {
      console.error(error);
      mutate(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    upload,
    data,
    progress,
    loading,
  };
};
